import { useEffect, useMemo, useState } from 'react';
import Breadcrumb from '@/components/base/Breadcrumb';
import EmptyState from '@/components/base/EmptyState';
import Pagination from '@/components/base/Pagination';
import SectionTitle from '@/components/base/SectionTitle';
import NewsListItem from '@/components/feature/NewsListItem';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { newsList } from '@/mocks/news';
import { getLatestNews } from '@/utils/media';

const PAGE_SIZE = 6;
const CATEGORIES = [
  { key: 'all', label: '全部' },
  { key: 'news', label: '新闻' },
  { key: 'trailer', label: '预告' },
  { key: 'feature', label: '专题' },
];

export default function NewsListPage() {
  useDocumentMeta(
    '影视资讯 - 黄牛视频',
    '黄牛视频影视资讯频道，提供最新影视新闻、预告片发布和深度专题内容，掌握行业动态与热门剧集更新。',
    '影视资讯,影视新闻,预告片,影视专题,黄牛视频',
  );

  const [category, setCategory] = useState('all');
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [category]);

  const filtered = useMemo(() => {
    const list =
      category === 'all' ? [...newsList] : newsList.filter((n) => n.category === category);
    return list.sort((a, b) => (a.publishedAt < b.publishedAt ? 1 : -1));
  }, [category]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const recommendations = getLatestNews(3);

  const chipBase = 'rounded-full px-4 py-1.5 text-sm transition-colors whitespace-nowrap';

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '影视资讯' }]} />

      <SectionTitle
        title="影视资讯"
        subtitle="最新影视新闻、预告发布与深度专题，第一时间掌握行业动态。"
        asHeading="h1"
      />

      <div className="mb-6 flex flex-wrap items-center gap-2 rounded-lg border border-background-200 bg-background-100 p-3">
        {CATEGORIES.map((c) => (
          <button
            key={c.key}
            type="button"
            onClick={() => setCategory(c.key)}
            className={`${chipBase} ${
              category === c.key
                ? 'bg-primary-500 text-background-50'
                : 'bg-background-200 text-foreground-300 hover:text-foreground-50'
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {pageItems.length === 0 ? (
        <EmptyState
          icon="ri-newspaper-line"
          title="该分类暂无资讯"
          description="试试切换其他分类，查看更多影视动态。"
          actionLabel="查看全部资讯"
          onAction={() => setCategory('all')}
        />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {pageItems.map((item) => (
              <NewsListItem key={item.id} item={item} />
            ))}
          </div>
          <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
        </>
      )}

      <section className="mt-12">
        <SectionTitle title="推荐阅读" icon="ri-bookmark-line" accent="accent" />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {recommendations.map((item) => (
            <NewsListItem key={item.id} item={item} withCover={false} />
          ))}
        </div>
      </section>
    </div>
  );
}